import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AIPane from "../../components/AIPane";
import {
  AI_PANE_MODE_INBOX,
  buildAIPaneSearch,
  isAIPaneOpen,
} from "../../utils/aiPaneUrl";
import {
  clearInboxList,
  fetchInboxItems,
  fetchInboxUnreadCount,
  inboxIdsInRange,
  selectInboxFilters,
  selectInboxItems,
  selectInboxPagination,
  selectSelectedInboxItem,
  setSelectedInboxDocument,
} from "../../slice/InboxSlice";
import InboxList from "./components/InboxList";
import InboxDocumentViewer from "./components/InboxDocumentViewer";
import InboxAIHeader from "./components/InboxAIHeader";
import InboxDuplicateBanner from "./components/InboxDuplicateBanner";
import MobileInbox from "./MobileInbox";
import useIsMobile from "../../hooks/useIsMobile";
import useMarkInboxReviewed from "../../hooks/useMarkInboxReviewed";
import useResizableWidth from "../../hooks/useResizableWidth";
import { markInboxClick } from "./inboxOpenStopwatch";
import {
  INBOX_AI_RAIL_COLLAPSED_WIDTH,
  INBOX_AI_RAIL_WIDTH,
  InboxAIPaneWrapper,
  InboxColumnResizer,
  InboxLayout,
  InboxListColumn,
  InboxPageWrapper,
} from "./InboxStyled";

// Left list column: user-draggable between these bounds; the chosen
// width is remembered in the browser. Right rail collapse state is
// remembered the same way.
const INBOX_LIST_WIDTH_STORAGE_KEY = "inboxListWidth";
const INBOX_LIST_DEFAULT_WIDTH = 360;
const INBOX_LIST_MIN_WIDTH = 260;
const INBOX_LIST_MAX_WIDTH = 720;
const INBOX_AI_RAIL_COLLAPSED_STORAGE_KEY = "inboxAIRailCollapsed";

// The document viewer in the middle must never be squeezed out by a
// remembered-wide list on a narrower screen (Bugbot, #809). The list's
// live ceiling is whatever the viewport leaves after the primary
// sidebar, the right rail at its current width, the column gaps, and
// this floor for the document — same arithmetic MatterDetails uses.
const PRIMARY_SIDEBAR_WIDTH = 72;
const INBOX_COLUMN_GAP = 8;
const INBOX_VIEWER_MIN_WIDTH = 400;

const readStoredCollapsed = () => {
  try {
    return globalThis.localStorage?.getItem(INBOX_AI_RAIL_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

/**
 * Attorney Inbox page (FE Phase 5a — read surface).
 *
 * 3-pane layout (mirrors `MatterDetails/TabView`'s shape so we can
 * reuse Apryse + AIPane verbatim):
 *
 *   ┌─ Inbox list (~360px) ─┬─ Apryse viewer (flex 1) ─┬─ AIPane (500px) ─┐
 *   │ flat chronological    │ single instance, swap   │ Briefing +        │
 *   │ rows; filter chips    │ doc on selection        │ Context Analysis  │
 *   └───────────────────────┴─────────────────────────┴───────────────────┘
 *
 * URL contract:
 *   - `/inbox`           → list visible, no row selected.
 *   - `/inbox/:documentId` → list visible, row selected, viewer + AI rail
 *                            populated for that doc.
 *   - `?ai=briefing|context-analysis` → controls the AIPane's active tab.
 *     Defaults to `?ai=briefing` on initial doc selection. The sparkle
 *     nav closes the rail outright (strips `?ai=`) and reopening
 *     re-applies the default. The chevron in the rail header instead
 *     COLLAPSES it to a narrow icon column (local preference, not URL)
 *     so the action buttons stay within reach.
 *
 * Layout preferences (list width, rail collapsed) persist in
 * localStorage; see `useResizableWidth`.
 *
 * Phase 5a write surface is intentionally absent — no action footer, no
 * mark-reviewed/snooze/task/message mutations. Phase 5b layers those
 * on top of this read surface against the `InboxSlice` thunks (which
 * Phase 5a left scaffolded).
 */
const Inbox = () => {
  const isMobile = useIsMobile();
  // Phone-sized screens get the stacked list → detail flow (Mobile
  // DMS v1, Phase 2) instead of the 3-pane desktop layout. Fork here
  // — before the desktop surface mounts Apryse — so mobile never pays
  // the WASM bootstrap. Hook-order safe: `DesktopInbox` owns all the
  // desktop hooks and mounts exclusively.
  return isMobile ? <MobileInbox /> : <DesktopInbox />;
};

const DesktopInbox = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { documentId: documentIdParam } = useParams();

  const items = useSelector(selectInboxItems);
  const pagination = useSelector(selectInboxPagination);
  const filters = useSelector(selectInboxFilters);
  const selectedItem = useSelector(selectSelectedInboxItem);
  const markReviewed = useMarkInboxReviewed();
  const [checkedIds, setCheckedIds] = useState([]);
  const anchorIdRef = useRef(null);

  // Mirror the URL's :documentId into Redux on every change. Source of
  // truth is the URL — the slice copy lets cross-component reads
  // (right rail header, viewer) stay cheap without prop-drilling.
  // Cast to integer for the slice (BE document IDs are int per
  // handoff §1, despite the URL string).
  useEffect(() => {
    dispatch(setSelectedInboxDocument(documentIdParam ?? null));
  }, [dispatch, documentIdParam]);

  // Initial inbox load + unread-count seed. Refire on filter changes
  // (matter scope, include-snoozed) but NOT on page changes — the list
  // pane drives its own pagination dispatches.
  //
  // We deliberately don't pass `page` here; the slice's default of
  // page 1 is correct on mount, and InboxList owns subsequent paging.
  useEffect(() => {
    dispatch(
      fetchInboxItems({
        matterId: filters.matterId,
        includeSnoozed: filters.includeSnoozed,
        recentlyReviewed: filters.recentlyReviewed,
        category: filters.category,
        page: 1,
        pageSize: 20,
      })
    );
    dispatch(fetchInboxUnreadCount());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filters.matterId, filters.includeSnoozed, filters.recentlyReviewed]);


  // Drop the cached list on unmount so the next mount of /inbox always
  // fetches fresh (cheap, and saves us from showing a stale page-3
  // snapshot when the user navigates back days later). Unread count
  // intentionally survives — the sidebar badge needs a value across
  // routes, and the count is server-authoritative anyway.
  useEffect(
    () => () => {
      dispatch(clearInboxList());
    },
    [dispatch]
  );

  // Whether the AI rail is open is URL-driven so the sparkle toggle in
  // PrimarySideBar can drive both surfaces (matter + inbox) with the
  // same `?ai=...` query state.
  const isAIOpen = isAIPaneOpen(location.search);

  // Collapsed is a second, local state layered on "open": the rail is
  // still there, just narrowed to its action icons. It is not in the
  // URL — it's a personal layout preference, not something to share in
  // a link — so it lives in localStorage like the column widths.
  const [isAICollapsed, setIsAICollapsed] = useState(readStoredCollapsed);
  const handleToggleAICollapsed = useCallback(() => {
    setIsAICollapsed((prev) => {
      const next = !prev;
      try {
        globalThis.localStorage?.setItem(
          INBOX_AI_RAIL_COLLAPSED_STORAGE_KEY,
          String(next)
        );
      } catch {
        // Preference just won't persist this session.
      }
      return next;
    });
  }, []);

  // Viewport width, tracked so the list ceiling follows window resizes.
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1920
  );
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    globalThis.addEventListener("resize", handleResize);
    return () => globalThis.removeEventListener("resize", handleResize);
  }, []);

  const railWidth = !isAIOpen
    ? 0
    : isAICollapsed
      ? INBOX_AI_RAIL_COLLAPSED_WIDTH
      : INBOX_AI_RAIL_WIDTH;
  const listMaxWidth = useMemo(() => {
    const available =
      viewportWidth -
      PRIMARY_SIDEBAR_WIDTH -
      railWidth -
      INBOX_VIEWER_MIN_WIDTH -
      INBOX_COLUMN_GAP * 2;
    return Math.max(
      INBOX_LIST_MIN_WIDTH,
      Math.min(INBOX_LIST_MAX_WIDTH, available)
    );
  }, [railWidth, viewportWidth]);

  // Left list width — drag the strip on its right edge.
  const {
    width: listWidth,
    isResizing: isListResizing,
    startResize: startListResize,
  } = useResizableWidth({
    storageKey: INBOX_LIST_WIDTH_STORAGE_KEY,
    defaultWidth: INBOX_LIST_DEFAULT_WIDTH,
    min: INBOX_LIST_MIN_WIDTH,
    max: listMaxWidth,
  });

  // Auto-open the AI rail on first row selection so the right side of
  // the screen doesn't sit empty when an attorney clicks an item.
  // Only fires when no `?ai=` param is set, so closing the rail
  // explicitly (and then clicking a different row) sticks. Keep this
  // distinct from the sparkle-nav handler — that one toggles open/
  // closed; this one only ever transitions closed → open on first
  // selection.
  useEffect(() => {
    if (!documentIdParam) return;
    if (isAIOpen) return;
    const next = buildAIPaneSearch(location.search, "briefing");
    navigate(`${location.pathname}${next}`, { replace: true });
    // We intentionally exclude `isAIOpen` and `location.search` from
    // the deps: this effect should only fire on row selection
    // (documentIdParam transitions). React Router sees the
    // navigate(replace: true) as a search-string update which would
    // cycle if isAIOpen were a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentIdParam, navigate, location.pathname]);

  const handleSelectDocument = useCallback(
    (id, event) => {
      if (id == null) return;
      const isToggle = Boolean(event?.metaKey || event?.ctrlKey);
      const isRange = Boolean(event?.shiftKey);
      if (isRange) {
        event?.preventDefault?.();
        const from = anchorIdRef.current ?? documentIdParam ?? id;
        setCheckedIds(inboxIdsInRange(items, from, id));
      } else if (isToggle) {
        event?.preventDefault?.();
        setCheckedIds((prev) => {
          const seed =
            prev.length > 0
              ? prev
              : documentIdParam
                ? [documentIdParam]
                : [];
          const next = new Set(seed.map(String));
          const key = String(id);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return [...next];
        });
        anchorIdRef.current = id;
      } else {
        setCheckedIds([]);
        anchorIdRef.current = id;
      }
      markInboxClick(id);
      navigate(`/inbox/${id}${location.search}`);
    },
    [documentIdParam, items, location.search, navigate]
  );

  const handleClearChecked = useCallback(() => {
    setCheckedIds([]);
  }, []);

  const handleMarkSelectionReviewed = useCallback(() => {
    if (!checkedIds.length) return;
    markReviewed(checkedIds)
      .then(() => {
        setCheckedIds([]);
      })
      .catch(() => {});
  }, [checkedIds, markReviewed]);

  // Selected item lookup: prefer the slice's memoized selector (which
  // scans the current page's items), but fall back to a derived value
  // composed of the URL id + a placeholder name when the row isn't on
  // the current page (e.g. user landed on /inbox/<id> from a deep
  // link to a doc that lives on page 3). The fallback is intentionally
  // sparse — the right rail's BriefingTab + ContextAnalysisTab fetch
  // their own per-doc data, so they don't need the row payload.
  //
  // Note: this also prevents an InboxAIHeader flash to "no doc" while
  // the BE list call is still in flight on first mount.
  const fallbackItem = useMemo(() => {
    if (!documentIdParam || selectedItem) return null;
    return { id: documentIdParam, name: "", matter: null };
  }, [documentIdParam, selectedItem]);

  const headerItem = selectedItem || fallbackItem;
  const activeDocumentId = selectedItem?.id ?? documentIdParam ?? null;
  const documentName = selectedItem?.name || "";

  // Light defensive `void` use — `items` and `pagination` are unused
  // by this top-level layout component but kept available for future
  // empty-state copy ("0 unread of 134"); pulling them in via the
  // selector keeps the component subscribed for cheap re-renders if
  // the slice patches.
  void items;
  void pagination;

  return (
    <InboxPageWrapper>
      <InboxLayout>
        <InboxListColumn $width={listWidth}>
          <InboxList
            selectedDocumentId={activeDocumentId}
            onSelectDocument={handleSelectDocument}
            checkedIds={checkedIds}
            onMarkSelectionReviewed={handleMarkSelectionReviewed}
            onClearChecked={handleClearChecked}
          />
          <InboxColumnResizer
            type="button"
            onMouseDown={startListResize}
            onTouchStart={startListResize}
            data-active={isListResizing ? "true" : undefined}
            aria-label="Resize inbox list"
            aria-orientation="vertical"
            title="Drag to resize"
          />
        </InboxListColumn>
        <InboxDocumentViewer
          documentId={activeDocumentId}
          documentName={documentName}
        />
        <InboxAIPaneWrapper
          $isOpen={isAIOpen}
          $collapsed={isAICollapsed}
          aria-hidden={!isAIOpen}
          data-collapsed={isAICollapsed ? "true" : undefined}
        >
          {isAIOpen && (
            <>
              {/* Header + duplicate banner need the full width; the
                  collapsed rail shows only the expand control and the
                  action icons. */}
              {!isAICollapsed && <InboxAIHeader item={headerItem} />}
              {/* Only from the list row's own payload: the fallback
                  item for a deep link to an off-page document has no
                  duplicate data, and inventing one would be worse
                  than showing none. */}
              {!isAICollapsed && selectedItem && (
                <InboxDuplicateBanner item={selectedItem} />
              )}
              <AIPane
                mode={AI_PANE_MODE_INBOX}
                activeDocumentId={activeDocumentId}
                checkedDocumentIds={checkedIds}
                onClearChecked={handleClearChecked}
                collapsed={isAICollapsed}
                onToggleCollapse={handleToggleAICollapsed}
              />
            </>
          )}
        </InboxAIPaneWrapper>
      </InboxLayout>
    </InboxPageWrapper>
  );
};

export default Inbox;
